<script setup lang="ts">
import type {
	RegionTab,
	TabNeighborhoodClickedPayload,
	ViewMoreClickedPayload
} from '~/components/CuratedListSimilarProperties';
import type {
	LinkClickedPayload,
	RegionCardClickedPayload,
	SimilarRegionClickedPayload
} from '~/components/CuratedListSimilarRegions';
import type { ListingTrackingContext } from '~/composables/useListingEventTracking';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { CuratedList } from '~~/core/entities/CuratedList';
import type { NuxtListPropertiesApiReturn } from '~~/server/api/properties/index.get';

import { useFetch } from '#app';
import { useEvents } from '~/composables/events/useEvents';
import { useAbsoluteUrl } from '~/composables/useAbsoluteUrl';
import { useManager } from '~/composables/useListing/manager';
import {
	buildCarouselTrackingContext,
	buildManagedListingTrackingContext
} from '~/composables/useListingEventTracking';
import { getPropertyTypeText } from '~/utils';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import { curatedListRegionsLinks } from '~~/data/links/regionLinks';

definePageMeta({
	layout: 'default-transparent'
});

const route = useRoute('colecao-slug');
const { slug } = route.params;
const CACHE_TTL = 1000 * 60 * 60;

onMounted(() => {
	window.history.scrollRestoration = 'manual';
});

const { data: curatedList } = await useFetch(`/api/curated_list/${slug}`, {
	onRequestError() {
		navigateTo('/404');
	},
	onResponse({ response }) {
		if (response.status === 404) {
			navigateTo('/404');
		}

		if (!response._data) {
			navigateTo('/404');
		}
	},
	cache: 'default',
	...getCacheAsyncDataConfig<CuratedList>(CACHE_TTL)
});

const { toAbsoluteUrl } = useAbsoluteUrl();

useSeoMeta({
	title: () =>
		`Coleção ${curatedList.value?.creator_details.name} | PilarHomes`,
	description: () => curatedList.value?.creator_details.headline,
	ogTitle: () =>
		`Coleção ${curatedList.value?.creator_details.name} | PilarHomes`,
	ogDescription: () => curatedList.value?.creator_details.headline,
	ogImage: () => toAbsoluteUrl(curatedList.value?.ogImage)
});

const initialFilters = computed<Partial<PropertyFilters>>(() => {
	return {
		curatedListId: curatedList.value?.id,
		showHideProperties: true
	};
});

const {
	filters,
	isFetching,
	properties,
	hasProperties,
	isFetchingForTheFirstTime,
	nextPage,
	updateFiltersManager,
	filteredCount,
	displayPagination
} = useManager({
	page: 'colecao',
	collection: slug,
	perPageOptions: {
		perPageFirstPage: 12,
		perPageNextPages: 12
	},
	initialFilters,
	config: {
		immediate: import.meta.server,
		shouldScrollToTopOnFiltersChange: false
	}
});

const trackingContext = computed<ListingTrackingContext>(() =>
	buildManagedListingTrackingContext({
		placement: 'colecao',
		filters: filters.value,
		filteredCount: filteredCount.value,
		page: displayPagination.value.page,
		perPage: displayPagination.value.perPage
	})
);

const propertyCountLabel = computed<string>(
	() =>
		`${filteredCount.value} ${getPropertyTypeText(filters.value, filteredCount.value).toLocaleLowerCase()}`
);

const shouldShowNextPageButton = computed<boolean>(() => {
	if (isFetchingForTheFirstTime.value) return false;

	return properties.value.length < filteredCount.value;
});

const { data: regionsReport, status: regionsReportStatus } = await useFetch(
	`/api/region/report/${curatedList.value?.id}`,
	{
		cache: 'default'
	}
);

const isFetchingRegionsReport = computed<boolean>(
	() => regionsReportStatus.value === 'pending'
);

const uniqueRegions = computed<RegionTab[]>(() => {
	const items =
		(regionsReport.value as Array<{
			region: string;
			state: string;
			city: string;
		}> | null) ?? [];

	return items.map(item => ({
		region: item.region,
		state: item.state,
		city: item.city
	}));
});

const currentTab = ref<RegionTab>();

const defaultPropertiesReturn = {
	properties: [] as NuxtListPropertiesApiReturn['properties'],
	pagination: {
		totalItems: 0,
		totalPages: 0,
		currentPage: 1,
		itemsPerPage: 12,
		page: 1,
		perPage: 12,
		filteredCount: 0
	},
	locationSlugFilters: []
} as NuxtListPropertiesApiReturn;

const commercialIds = computed<string[]>(() =>
	properties.value.map(property => property.commercialId)
);

const { payload: cache } = useNuxtApp();
const key = computed(
	() =>
		`collection:${curatedList.value?.id}:${currentTab.value?.region || 'default'}`
);

function getCachedSimilarRegionProperties(): NuxtListPropertiesApiReturn | null {
	const data = cache?.data[key.value];

	if (!data) return null;

	const fetchedAt = data.fetchedAt ? new Date(data.fetchedAt) : null;
	if (!fetchedAt) return null;

	const expirationDate = new Date(fetchedAt.getTime() + CACHE_TTL);
	const isExpired = expirationDate.getTime() < Date.now();

	if (isExpired) return null;

	return data;
}

function setCachedData(data: NuxtListPropertiesApiReturn) {
	cache.data[key.value] = {
		...data,
		fetchedAt: new Date().toISOString()
	};
}

const {
	data: similarRegionProperties,
	status: similarRegionPropertiesStatus,
	execute: fetchSimilarRegionProperties
} = useLazyAsyncData<NuxtListPropertiesApiReturn>(
	'collection',
	async () => {
		const cachedProperties = getCachedSimilarRegionProperties();

		if (cachedProperties) return cachedProperties;

		await until(currentTab).toMatch(value => !!value?.region);

		if (!currentTab.value) {
			return defaultPropertiesReturn;
		}

		const { user } = useUserSession();
		const [similarRegionPropertiesApiReturn, similarRegionPropertiesError] =
			await nuxtPropertiesRepository.list({
				regions: [currentTab.value.region],
				perPage: 6,
				excludeProperties: commercialIds.value,
				markUserWishlistsId: user.value?.id
			});

		if (!similarRegionPropertiesApiReturn || similarRegionPropertiesError) {
			return defaultPropertiesReturn;
		}

		setCachedData(similarRegionPropertiesApiReturn);
		return similarRegionPropertiesApiReturn;
	},
	{
		immediate: false,
		default: () => defaultPropertiesReturn,
		...getCacheAsyncDataConfig(CACHE_TTL)
	}
);

watch(
	currentTab,
	newValue => {
		if (newValue) {
			fetchSimilarRegionProperties();
		}
	},
	{ immediate: true }
);

watchOnce(
	uniqueRegions,
	async () => {
		await until(uniqueRegions).toMatch(value => !!value.length);
		if (!currentTab.value) {
			currentTab.value = uniqueRegions.value[0] ?? undefined;
		}
	},
	{ immediate: true }
);

function updateOrdenation(event: {
	sortBy: PropertyFilters['sortBy'];
	orderBy: PropertyFilters['orderBy'];
}) {
	updateFiltersManager(event);
}

const {
	trackCardNeighborhoodClicked,
	trackCtaViewMoreClicked,
	trackTabNeighborhoodClicked,
	trackLinkClicked
} = useEvents();

const tabNeighborhoodBus = onClientEventBus(
	useEventBus<TabNeighborhoodClickedPayload>('tab-neighborhood-clicked'),
	payload => {
		trackTabNeighborhoodClicked({
			...payload,
			current_page: `Coleção ${curatedList.value?.creator_details.name}`,
			section_name: 'similar properties'
		});
	}
);

const linkClickedBus = onClientEventBus(
	useEventBus<LinkClickedPayload>('link-clicked'),
	payload => {
		trackLinkClicked({
			...payload,
			current_page: `Coleção ${curatedList.value?.creator_details.name}`,
			section_name: 'neighborhoods'
		});
	}
);

async function handleRegionClicked(payload: SimilarRegionClickedPayload) {
	trackCardNeighborhoodClicked({
		...payload,
		current_page: `Coleção ${curatedList.value?.creator_details.name}`,
		section_name: 'neighborhoods'
	});

	await navigateTo(payload.destination_url);
}

async function handleRegionCardClicked(payload: RegionCardClickedPayload) {
	await navigateTo(payload.destinationUrl);
}

function handleViewMoreClicked(payload: ViewMoreClickedPayload) {
	trackCtaViewMoreClicked({
		...payload,
		current_page: `Coleção ${curatedList.value?.creator_details.name}`,
		section_name: 'neighborhoods'
	});
}

const isSimilarRegionPropertiesLoading = computed<boolean>(() => {
	return similarRegionPropertiesStatus.value === 'pending';
});

const similarPropertiesTrackingContext = computed<ListingTrackingContext>(() =>
	buildCarouselTrackingContext({
		placement: 'carrossel_colecao',
		properties: (similarRegionProperties.value as any)?.properties ?? []
	})
);

onUnmounted(() => {
	// Cleanup event listeners
	tabNeighborhoodBus();
	linkClickedBus();
});
</script>

<template>
	<LazyCuratedListHeroSection :curated-list="curatedList" />

	<div class="relative w-full divide-y divide-white-dark-2">
		<Suspense>
			<template #default>
				<div class="divide-y divide-white-dark-2">
					<LazyCuratedListPropertyList
						:curated-list="curatedList"
						:filters="filters"
						:is-fetching="isFetching"
						:properties="properties"
						:has-properties="hasProperties"
						:is-fetching-for-the-first-time="isFetchingForTheFirstTime"
						:next-page="nextPage"
						:update-filters-manager="updateOrdenation"
						:property-count-label="propertyCountLabel"
						:should-show-next-page-button="shouldShowNextPageButton"
						:tracking-context="trackingContext"
					/>

					<!-- Explore imóveis semelhantes: SSR mostra skeleton, Client mostra dados reais -->
					<ClientOnly>
						<template #fallback>
							<!-- SSR: Sempre mostra seção com skeleton -->
							<LazyCuratedListSimilarProperties
								:similar-region-properties="[]"
								:unique-regions="[]"
								:is-loading="true"
								button-variant="secondary"
							/>
						</template>

						<!-- Client: Mostra seção com dados reais -->
						<LazyCuratedListSimilarProperties
							v-model:current-tab="currentTab"
							data-nuxt-delay-hydration
							:similar-region-properties="
								(similarRegionProperties as any)?.properties ?? []
							"
							:unique-regions="uniqueRegions"
							:is-loading="isSimilarRegionPropertiesLoading"
							:identifier="curatedList?.id"
							button-variant="secondary"
							:tracking-context="similarPropertiesTrackingContext"
							@cta-view-more-clicked="handleViewMoreClicked"
						/>
					</ClientOnly>

					<LazyCuratedListSimilarRegions
						:regions-report="regionsReport"
						:is-loading="isFetchingRegionsReport"
						button-variant="secondary"
						@region-clicked="handleRegionClicked"
						@region-card-clicked="handleRegionCardClicked"
					/>
				</div>
			</template>
			<template #fallback>
				<div class="flex w-full items-center justify-center py-10">
					<IconPilarCircularProgress
						class="size-10 animate-spin text-primary"
					/>
				</div>
			</template>
		</Suspense>

		<LazyCuratedListAboutSection :curated-list="curatedList" />

		<Container class="bg-white-dark-2">
			<div class="flex flex-col gap-6 lg:gap-10">
				<span
					class="font-matter-sq text-2xl font-normal tracking-[-0.03rem] text-primary md:text-[1.75rem] md:tracking-[-0.035rem]"
				>
					Descubra imóveis nos bairros mais desejados
				</span>

				<LazyNavigationSectionNavigation
					class="justify-start py-0!"
					:card="null"
					:items="curatedListRegionsLinks"
					color="primary"
					links-container-variant="default"
					links-title-classes="hidden"
				/>
			</div>
		</Container>
	</div>
</template>
